import {
	BusyIndicator,
	ContextActionBar,
	NoResults
} from "client/Components";
import {
	AlertManager,
	useAlerts,
	useForceRerender,
	useUserTimezone
} from "client/Hooks";
import {
	isNotNilOrEmpty
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState,
	useMemo,
	FunctionComponent
} from "react";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	navigateRelativeToEnvironment,
	navigateTo
} from "client_legacy/ndaw/navigator";
import { EmbedAppPageModel, getModel } from "client/Api/Apps/Embed/embedApp";
import { usePageTitle } from "client/Hooks/usePageTitle";
import styles from "./EmbedAppPage.module.less";
import { handlePageDataLoadingError, setHttpError } from "../ErrorPage/errorStore";
import {
	useGlobalNavigationStore,
	GlobalNavigationCriteria
} from "../Layout/TitleBar/GlobalNavigation/globalNavigationStore";
import {
	route,
	RouteParametersFromProps,
	connectToRouter
} from "client/Navigation";
import { tryParseInt } from "client/Misc/parseUtils";
import { AnalyticsAppEventData } from "client/Api/Analytics/analytics";
import { useAnalyticsStore } from "../Analytics/analyticsStore";
import { TimeZone } from "client/DateAndTime/timezones";
import { generateMaintenanceMessage } from "../Workspace/useTileTooltip";
import { ContentLoadingState, useEmbedContentLoading } from "./useEmbedContentLoading";
import { EmbedAppContent } from "./EmbedAppContent";
import { never } from "client_legacy/ndaw/errors";

export type EmberAppPageProps = {
	appId: number;
};

export const EmbedAppPage = (props: EmberAppPageProps): ReactElement => {
	const {
		appId
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [reload, loadVersion] = useForceRerender();
	const [model, setModel] = useState<EmbedAppPageModel>();
	const alertManager: AlertManager = useAlerts();
	const timezone: TimeZone = useUserTimezone();
	const addAnalyticsAppEvent: (data: AnalyticsAppEventData) => void = useAnalyticsStore(x => x.addAppEvent);

	const setActiveGlobalNavigationCriteria: (criteria: GlobalNavigationCriteria) => () => void = useGlobalNavigationStore(
		x => x.setActiveCriteria
	);

	usePageTitle(model?.title ?? "");

	useEffect(
		() => {
			hasImplicitDependencyOn(loadVersion);
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const data: EmbedAppPageModel = await getModel(appId, signal)
						.catch(handlePageDataLoadingError);

					setModel(data);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}

			const cancellation = new AbortController();
			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			appId,
			loadVersion
		]
	);

	const isUnavailable: boolean = !model
		|| model.conditionalAccess.isSet
		|| model.maintenanceWindow.isSet;

	useEffect(
		(): EffectWithDestructorResult => {
			if (isUnavailable) {
				return;
			}

			addAnalyticsAppEvent({
				installedAppId: appId
			});

			return setActiveGlobalNavigationCriteria({
				type: "embedApp",
				installedAppId: appId
			});
		},
		[
			appId,
			isUnavailable,
			setActiveGlobalNavigationCriteria,
			addAnalyticsAppEvent
		]
	);

	const navigateToHomeHandler = useCallback(
		(): void => {
			navigateRelativeToEnvironment("/", false);
		},
		[]
	);

	const renderContent = (): ReactElement => {
		if (isBusy || !model) {
			return <BusyIndicator />;
		}

		if (model.conditionalAccess.isSet) {
			return (
				<NoResults
					icon="ban"
					title={localize("_SpaceIsNotAccessible")}
					message={
						model.conditionalAccess
							.value
							.reason
					}
					retryText={localize("_Back")}
					onRetry={navigateToHomeHandler}
					height="fill"
				/>
			);
		}

		if (model.maintenanceWindow.isSet) {
			return (
				<NoResults
					icon="screwdriver-wrench"
					title={localize("_SpaceIsInMaintenance")}
					message={
						generateMaintenanceMessage(
							"space",
							model.maintenanceWindow
								.value,
							timezone
						)
					}
					retryText={localize("_Back")}
					onRetry={navigateToHomeHandler}
					height="fill"
				/>
			);
		}

		return (
			<AvailablePageContent
				model={model}
				onReload={reload}
			/>
		);
	};

	return (
		<div id="main-content" className={styles.root}>
			{renderContent()}
		</div>
	);
};
EmbedAppPage.displayName = "EmbedAppPage";

type AvailablePageContentProps = {
	model: EmbedAppPageModel;
	onReload: () => void;
};

const AvailablePageContent = (props: AvailablePageContentProps): ReactElement => {
	const {
		model,
		onReload
	} = props;

	const {
		appId
	} = model;

	const loadingState: ContentLoadingState = useEmbedContentLoading(appId, true);

	const actions = useMemo(
		(): readonly Action<EmbedAppPageModel>[] => [
			{
				name: "refresh",
				title: localize("_Refresh"),
				icon: "arrow-rotate-right",
				kind: ActionKind.Regular,
				execute: onReload,
				canExecute: () => true
			},
			{
				name: "goToWebsite",
				title: localize("_GoToWebsite"),
				icon: "share-from-square",
				kind: ActionKind.Regular,
				execute: ctx => navigateTo(
					ctx.navigationUrl!,
					true
				),
				canExecute: ctx => isNotNilOrEmpty(ctx.navigationUrl)
			}
		],
		[onReload]
	);

	useEffect(
		(): void => {
			if (loadingState.kind === "forbidden") {
				setHttpError({ statusCode: 403 });
			}
		},
		[loadingState.kind]
	);

	const renderContent = (): ReactElement => {
		switch (loadingState.kind) {
			case "not-started":
			case "pending":
				return <BusyIndicator />;
			case "loaded":
				const { kind, html } = loadingState.model;

				return (
					<EmbedAppContent
						kind={kind}
						html={html}
						trackingId="content"
					/>
				);
			case "error":
			case "forbidden":
				return (
					<NoResults
						icon="gear"
						title={localize("_ErrorLoadingTheData")}
						message={localize("_ErrorDuringDataRetrieval")}
						onRetry={onReload}
						height="fill"
					/>
				);
			default:
				never(loadingState);
		}
	};

	return (
		<>
			<ContextActionBar
				actions={actions}
				context={model}
				hasBorderBottom
				trackingId="global-action-bar"
			/>

			<div className={styles.content}>
				{renderContent()}
			</div>
		</>
	);
};
AvailablePageContent.displayName = "AvailablePageContent";

export type EmberAppRouteParams = RouteParametersFromProps<
	EmberAppPageProps,
	"appId"
>;

export const EmbedAppPageForRouter: FunctionComponent = connectToRouter(
	EmbedAppPage,
	{
		"appId": {
			isRequired: true,
			tryParse: tryParseInt
		}
	}
);

export const embedAppRouting = {
	page: route<EmberAppRouteParams>`/WebContent/${"appId"}`
};